import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Icons } from '@/shared/constants/icons';
import { LAYOUT_ID, SPRING, motionSafe } from '@/shared/constants/motion';
import { useInboxBadges } from '@/shared/hooks/useInboxBadges';
import { useAttentionCount } from '@/shared/hooks/useAttention';
import { DESKTOP_QUERY, WIDE_QUERY, useMatchMedia } from '@/shared/hooks/useMatchMedia';
import { useNavStore } from '@/shared/stores/nav.store';
import { openItems, useCurrentPlan } from '@/shared/hooks/usePlan';
import { useCanWrite } from '@/shared/hooks/useCanWrite';
import { CLIENT_FOOTER, NAV_FOOTER, folderOf, hasDeeperDestination, inDestination, sectionsFor, type NavEntry, type NavLeaf } from './nav-model';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onCompose: () => void;
  /** Opens the command palette. Only rendered in the phone drawer, where the top bar has no search. */
  onSearch?: () => void;
  /**
   * A white-label workspace that keeps "Powered by UniPostudio" on. Off for
   * one that switched it off, and for every workspace without white label,
   * where the whole app already is UniPostudio.
   */
  showPoweredBy?: boolean;
}

type NavItem = NavLeaf;

/** Rail width when collapsed to icons; it is 232px with labels. */
export const RAIL_WIDTH = 72;

function Count({ count, tone }: { count: number; tone: 'accent' | 'warn' | 'bad' }) {
  if (count <= 0) return null;
  return (
    <span
      className="relative z-10 text-caption font-semibold px-[6px] py-[1px] rounded-full leading-[1.4] shrink-0 tabular-nums"
      style={
        tone === 'accent'
          ? { background: 'var(--color-accent)', color: '#fff' }
          : tone === 'bad'
            ? { background: 'var(--bad)', color: '#fff' }
            : { background: 'var(--warn-soft)', color: 'var(--color-warning-text)' }
      }
      aria-hidden="true"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface ItemProps {
  item: NavItem;
  expanded: boolean;
  /** Small count shown beside the label when expanded. */
  count?: number;
  countTone?: 'accent' | 'warn' | 'bad';
  /** Red dot on the icon: something is escalated to me. Shown even when collapsed. */
  dot?: boolean;
  /** What the count and the dot say to a screen reader (they are aria-hidden). */
  srText?: string;
  onNavigate: () => void;
  /** Shown as active although the route is a sibling (a folded folder whose page is open). */
  forceActive?: boolean;
}

function Item({ item, expanded, count = 0, countTone = 'accent', dot, srText, onNavigate, forceActive = false }: ItemProps) {
  const { t } = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <NavLink
      to={item.path}
      end={hasDeeperDestination(item.path)}
      onClick={onNavigate}
      title={expanded ? undefined : label}
      className={({ isActive: routeActive }) => {
        const isActive = routeActive || forceActive;
        return [
          'relative flex items-center gap-3 rounded-[10px] no-underline w-full overflow-hidden',
          'min-h-[40px] pointer-coarse:min-h-[44px] text-ui',
          expanded ? 'px-3' : 'justify-center px-0',
          isActive ? 'text-[var(--ink)] font-semibold' : 'text-[var(--ink-2)] font-medium hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]',
        ].join(' ');
      }}
    >
      {({ isActive: routeActive }) => {
        const isActive = routeActive || forceActive;
        return (
        <>
          {isActive && (
            <motion.span
              layoutId={LAYOUT_ID.sidebarActive}
              className="absolute inset-0 rounded-[10px] bg-[var(--color-accent-soft)]"
              transition={motionSafe(SPRING, reduced)}
              aria-hidden="true"
            />
          )}
          {isActive && (
            <span className="absolute start-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-e-full bg-[var(--color-accent)]" aria-hidden="true" />
          )}
          <span className="relative z-10 shrink-0 inline-flex">
            <Icon size={20} strokeWidth={1.5} aria-hidden="true" />
            {dot && <span data-testid="escalated-dot" className="absolute -top-0.5 -end-0.5 w-2 h-2 rounded-full bg-[var(--bad)] ring-2 ring-[var(--color-surface)]" aria-hidden="true" />}
            {!expanded && count > 0 && (
              <span className="absolute -top-1.5 -end-2 text-[10px] font-semibold px-[5px] rounded-full leading-[1.5] tabular-nums" style={{ background: 'var(--color-accent)', color: '#fff' }} aria-hidden="true">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </span>
          {/* The label stays in the DOM so the link keeps its name; it is
              clipped by the rail width until the rail expands. */}
          {/* Collapsed: screen-reader text only. `relative` beside `sr-only` won the
              cascade, so the hidden label kept its width + gap and pushed the
              icon off-centre in the 72px rail. */}
          <span className={expanded ? 'relative z-10 flex-1 truncate whitespace-nowrap' : 'sr-only'}>{label}</span>
          {expanded && count > 0 && <Count count={count} tone={countTone} />}
          {srText && <span className="sr-only">{srText}</span>}
        </>
        );
      }}
    </NavLink>
  );
}

interface FolderProps {
  entry: NavEntry & { children: NavLeaf[] };
  expanded: boolean;
  open: boolean;
  onToggle: (open: boolean) => void;
  countFor: (key: string) => number;
  onNavigate: () => void;
}

/**
 * A menu folder (UI v2): its pages listed below it when open. Folded to the
 * icon rail it is a single link to its first page, active while any of its
 * pages is shown, carrying the sum of its pages' counts.
 */
function Folder({ entry, expanded, open, onToggle, countFor, onNavigate }: FolderProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const Icon = entry.icon;
  const active = entry.children.some((c) => inDestination(pathname, c.path));
  const total = entry.children.reduce((n, c) => n + countFor(c.key), 0);
  if (!expanded) {
    return <Item item={entry} expanded={false} count={total} onNavigate={onNavigate} forceActive={active} />;
  }
  const listId = `nav-folder-${entry.key}`;
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          const next = !open;
          onToggle(next);
          // Opening a folder from elsewhere also lands on its first page: one click, somewhere useful.
          if (next && !active) {
            navigate(entry.children[0]?.path ?? entry.path);
            onNavigate();
          }
        }}
        className={[
          'relative flex items-center gap-3 rounded-[10px] w-full px-3 min-h-[40px] pointer-coarse:min-h-[44px] text-ui bg-transparent border-none cursor-pointer text-start',
          active ? 'text-[var(--ink)] font-semibold' : 'text-[var(--ink-2)] font-medium hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]',
        ].join(' ')}
      >
        <Icon size={20} strokeWidth={1.5} className="shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate whitespace-nowrap">{t(entry.labelKey)}</span>
        {!open && total > 0 && <Count count={total} tone="accent" />}
        <Icons.chevronDown size={16} className={['shrink-0 text-[var(--ink-3)] transition-transform', open ? 'rotate-180' : ''].join(' ')} aria-hidden="true" />
      </button>
      {open && (
        <div id={listId} className="flex flex-col gap-0.5 mt-0.5 mb-1" role="group" aria-label={t(entry.labelKey)}>
          {entry.children.map((c) => (
            <NavLink
              key={c.key}
              to={c.path}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  'relative flex items-center gap-2 rounded-[10px] no-underline ps-11 pe-3 min-h-[36px] pointer-coarse:min-h-[44px] text-ui',
                  isActive ? 'bg-[var(--color-accent-soft)] text-[var(--ink)] font-semibold' : 'text-[var(--ink-2)] hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]',
                ].join(' ')
              }
            >
              <span className="flex-1 truncate">{t(c.labelKey)}</span>
              <Count count={countFor(c.key)} tone="accent" />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The app rail (blueprint C01, DS04): labelled groups — Home and Inbox, Publish,
 * Library, Insights, Brand — with Settings and Help at the foot. From 1280px
 * the labels show by default (232px) unless collapsed, which is remembered;
 * collapsed (and below 1280px) it is a 72px icon rail whose icons name
 * themselves in a tooltip — it never floats over the page, which used to
 * cover the content's left edge. The phone/tablet drawer always shows labels. The badge on
 * Inbox is the unread count, with a red dot when anything is escalated to me.
 */
export function Sidebar({ open, onClose, onCompose, onSearch, showPoweredBy = false }: SidebarProps) {
  const { t } = useTranslation();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { unread, escalated } = useInboxBadges();
  const attentionCount = useAttentionCount();
  const { data: planData } = useCurrentPlan();
  const planOpen = openItems(planData?.plan).length;
  // Internal staff only. The route and every /admin API are guarded separately.
  // Viewers and clients write nothing: no Compose. Clients see the review portal (PC-26).
  const { canWrite, role } = useCanWrite();
  const client = role === 'client';
  // A role never sees a destination the API refuses it (AL-06): a client's
  // portal keeps approvals and the calendar; analytics only where the API serves it.
  const sections = sectionsFor(role);
  const footer = client ? CLIENT_FOOTER : NAV_FOOTER;
  const { pathname } = useLocation();
  const folders = useNavStore((s) => s.folders);
  const setFolder = useNavStore((s) => s.setFolder);
  // The folder of the page on screen opens by itself; one closed by hand stays closed until a page in it is opened again.
  const currentFolder = folderOf(pathname, sections);
  const [autoOpen, setAutoOpen] = useState<string | null>(currentFolder);
  useEffect(() => setAutoOpen(currentFolder), [currentFolder]);
  const isOpen = (key: string): boolean => (autoOpen === key ? true : (folders[key] ?? false));
  const countFor = (key: string): number => (key === 'inbox' ? unread : key === 'overview' ? attentionCount : key === 'plan' ? planOpen : 0);

  // Docked with labels on a wide screen unless collapsed; labels in the drawer.
  // No hover/focus peek: it floated over the page and covered its left edge.
  const wide = useMatchMedia(WIDE_QUERY);
  const collapsed = useNavStore((s) => s.collapsed);
  const toggleCollapsed = useNavStore((s) => s.toggleCollapsed);
  const pinned = wide && !collapsed;
  const expanded = open || pinned;

  // Below lg the closed drawer is only moved off-screen: it is made inert so
  // its links leave the tab order and the accessibility tree. While open,
  // Escape closes it, focus moves into it, and returns to the opener after.
  const isDesktop = useMatchMedia(DESKTOP_QUERY);
  const asideRef = useRef<HTMLElement>(null);
  const hiddenDrawer = !isDesktop && !open;
  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    if (hiddenDrawer) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    }
  }, [hiddenDrawer]);
  useEffect(() => {
    if (isDesktop || !open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const raf = requestAnimationFrame(() => asideRef.current?.querySelector<HTMLElement>('a[href], button')?.focus());
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, [open, isDesktop]);

  return (
    <aside
      ref={asideRef}
      id="app-sidebar"
      className={[
        'fixed start-0 top-[64px] bottom-0 bg-[var(--color-surface)] border-e border-[var(--line)] overflow-y-auto overflow-x-hidden z-[45] py-3 px-3 flex flex-col',
        'lg:translate-x-0',
        open ? 'translate-x-0' : 'max-lg:-translate-x-full rtl:max-lg:translate-x-full',
        'max-lg:w-[min(280px,80vw)]',
        expanded ? 'lg:w-[232px]' : 'lg:w-[72px]',
      ].join(' ')}
      style={{
        transition: 'transform var(--motion-panel), width var(--motion-micro)',
      }}
      data-expanded={expanded ? 'true' : 'false'}
      data-pinned={pinned ? 'true' : 'false'}
      aria-label={t('nav.sidebar')}
    >
      <nav aria-label={t('nav.main')} className="flex flex-col gap-0.5">
        {/* Phones have no search in the top bar (blueprint S90), so the drawer carries it. */}
        {onSearch && (
          <button
            type="button"
            onClick={() => { onClose(); onSearch(); }}
            className="md:hidden flex items-center gap-3 min-h-[44px] mb-1.5 px-3 rounded-[10px] border border-[var(--line)] bg-[var(--color-surface-2)] text-[var(--ink-3)] text-ui cursor-pointer w-full hover:border-[var(--line-2)]"
            aria-label={t('topbar.searchShortcut')}
          >
            <Icons.search size={20} className="shrink-0" strokeWidth={1.5} aria-hidden="true" />
            <span className="flex-1 text-start truncate">{t('topbar.search')}</span>
          </button>
        )}
        {sections.map((sec, si) => (
          <div key={sec.key} role="group" className="flex flex-col gap-0.5">
            {si > 0 && <div className="mx-3 my-2 border-t border-[var(--line)]" aria-hidden="true" />}
            {sec.entries.map((entry) =>
              entry.children ? (
                <Folder
                  key={entry.key}
                  entry={entry as NavEntry & { children: NavLeaf[] }}
                  expanded={expanded}
                  open={isOpen(entry.key)}
                  onToggle={(o) => {
                    setFolder(entry.key, o);
                    if (!o && autoOpen === entry.key) setAutoOpen(null);
                  }}
                  countFor={countFor}
                  onNavigate={onClose}
                />
              ) : (
                <Item
                  key={entry.key}
                  item={entry}
                  expanded={expanded}
                  onNavigate={onClose}
                  count={countFor(entry.key)}
                  countTone={entry.key === 'overview' ? 'warn' : 'accent'}
                  dot={entry.key === 'inbox' && escalated > 0}
                  srText={
                    entry.key === 'inbox'
                      ? [unread > 0 ? t('nav.unreadCount', { count: unread }) : '', escalated > 0 ? t('nav.escalatedCount', { count: escalated }) : ''].filter(Boolean).join(', ') || undefined
                      : entry.key === 'overview' && attentionCount > 0
                        ? t('nav.attentionCount', { count: attentionCount })
                        : undefined
                  }
                />
              ),
            )}
          </div>
        ))}

        {/* Compose is reached from the top bar; only the drawer (phones and
            tablets) keeps a quiet copy, so desktop shows one Compose (DS03). */}
        {canWrite && <button
          type="button"
          onClick={() => { onClose(); onCompose(); }}
          title={expanded ? undefined : t('nav.compose')}
          className={[
            'lg:hidden flex items-center gap-3 min-h-[44px] my-1.5 rounded-[10px] border border-[var(--color-accent-border)] bg-[var(--color-accent-soft)] text-[var(--color-accent-text)] text-body font-semibold cursor-pointer w-full hover:border-[var(--color-accent)] overflow-hidden',
            expanded ? 'px-3' : 'justify-center px-0',
          ].join(' ')}
        >
          <Icons.add size={20} className="shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <span className={expanded ? 'flex-1 text-start truncate' : 'sr-only'}>{t('nav.compose')}</span>
        </button>}
      </nav>

      <div className="flex-1 min-h-4" />

      <nav aria-label={t('nav.more')} className="flex flex-col gap-0.5 border-t border-[var(--line)] pt-3">
        {footer.map((item) => (
          <Item key={item.key} item={item} expanded={expanded} onNavigate={onClose} />
        ))}
        {/* Wide screens only: keep the labels, or fold the rail to icons. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          className={['hidden xl:flex items-center gap-3 min-h-[40px] pointer-coarse:min-h-[44px] rounded-[10px] text-ui font-medium text-[var(--ink-3)] bg-transparent border-none cursor-pointer w-full hover:bg-[var(--color-surface-3)] hover:text-[var(--ink)]', expanded ? 'px-3' : 'justify-center px-0'].join(' ')}
          title={expanded ? undefined : t('nav.expand')}
        >
          {collapsed ? <Icons.panelExpand size={20} strokeWidth={1.5} className="shrink-0 rtl:flip" aria-hidden="true" /> : <Icons.panelCollapse size={20} strokeWidth={1.5} className="shrink-0 rtl:flip" aria-hidden="true" />}
          <span className={expanded ? 'flex-1 text-start truncate' : 'sr-only'}>{collapsed ? t('nav.expand') : t('nav.collapse')}</span>
        </button>
      </nav>

      {showPoweredBy && (
        <p
          data-testid="powered-by"
          className={['m-0 pt-3 px-3 text-caption text-[var(--ink-3)] truncate', expanded ? '' : 'sr-only'].join(' ')}
        >
          {t('branding.poweredByLine')}
        </p>
      )}
    </aside>
  );
}
